/**
 * 通用确认弹窗（CLAUDE.md §前端 UI 须借鉴 LEO v1.0：§6.1 按钮层级 / §6.6 弹窗与浮层）。
 *
 * 为什么：「删除」等破坏性操作需二次确认防误触；仓库此前无现成确认组件（未使用
 * window.confirm），抽出共享件供华为 / Apple 策略配置等页面复用，避免逐页复制。
 *
 * 样式口径：fixed 居中浮层（bg-black/45 遮罩，z-170 高于脑图全屏层 z-140 与
 * tooltip z-160）+ leo-card 容器；取消用 leo-btn-ghost（次要按钮）；危险操作
 * （danger=true）确认按钮为红底（静态 #dc2626，两主题通用，与 index.css
 * .leo-btn-ghost.is-danger 同色）。
 *
 * 行为：点击遮罩 / ESC = onCancel；确认按钮 loading 时禁用防重复提交。
 */
import { useEffect } from "react";
import type { ReactNode } from "react";

interface ConfirmDialogProps {
  /** 是否展示。 */
  open: boolean;
  /** 标题（如「确认删除策略」）。 */
  title: string;
  /** 正文描述（可含 JSX，如高亮策略 id）。 */
  description: ReactNode;
  /** 确认按钮文案（默认「确认」）。 */
  confirmText?: string;
  /** 取消按钮文案（默认「取消」）。 */
  cancelText?: string;
  /** 危险操作（删除等）：确认按钮红底。 */
  danger?: boolean;
  /** 提交中：禁用确认按钮防重复点击。 */
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmDialog({
  open,
  title,
  description,
  confirmText = "确认",
  cancelText = "取消",
  danger = false,
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  // 打开期间 ESC = 取消（监听随 open 挂载/卸载，不污染全局）
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[170] flex items-center justify-center bg-black/45"
      onClick={onCancel}
    >
      <div
        className="leo-card w-[min(92vw,380px)] rounded-xl p-5"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-display text-[14px] font-semibold text-text-emphasis">
          {title}
        </div>
        <div className="mt-2 text-[12.5px] leading-relaxed text-text-body">{description}</div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="leo-btn-ghost px-4 py-1.5 text-[12.5px]"
            onClick={onCancel}
          >
            {cancelText}
          </button>
          <button
            type="button"
            className={
              danger
                ? "rounded-lg bg-[#dc2626] px-5 py-1.5 text-[12.5px] font-medium text-white transition-colors hover:bg-[#b91c1c] disabled:cursor-not-allowed disabled:opacity-60"
                : "leo-cta-primary w-auto px-5 py-1.5 text-[12.5px]"
            }
            disabled={loading}
            onClick={onConfirm}
          >
            {loading ? "提交中…" : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
